import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { hasRoles, getCurrentUser } from '../../utils/roleCheck';
import './WorkspaceManagement.css';

function WorkspaceManagementPage() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    workspaceName: '',
    description: '',
    isActive: true
  });
  
  // Delete confirmation
  const [deleteNumbers, setDeleteNumbers] = useState(['', '', '']);
  const [deleteError, setDeleteError] = useState(null);
  const [graphCount, setGraphCount] = useState(0);
  
  // Permissions state
  const [permissions, setPermissions] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPermissionType, setSelectedPermissionType] = useState('Read');
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Check if current user can create workspaces
  const user = getCurrentUser();
  const canCreateWorkspaces = hasRoles(user?.roles, ['CerebralAdmin', 'CompanyAdmin', 'WorkspaceAdmin']);

  // Load initial data
  useEffect(() => {
    loadWorkspaces();
  }, [currentPage, pageSize]);

  // Filter workspaces based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkspaces(workspaces);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = workspaces.filter(workspace =>
        workspace.workspaceName?.toLowerCase().includes(term) ||
        workspace.description?.toLowerCase().includes(term) ||
        workspace.createUser?.toLowerCase().includes(term)
      );
      setFilteredWorkspaces(filtered);
    }
  }, [searchTerm, workspaces]);

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.listWorkspaces(currentPage, pageSize);
      
      if (response.success) {
        setWorkspaces(response.workspaces || []);
        setTotalCount(response.totalCount || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError('Failed to load workspaces');
      }
    } catch (err) {
      console.error('Error loading workspaces:', err);
      setError(err.response?.data?.message || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkspace = () => {
    setFormData({
      workspaceName: '',
      description: '',
      isActive: true
    });
    setShowCreateModal(true);
  };

  const handleEditWorkspace = (workspace) => {
    setSelectedWorkspace(workspace);
    setFormData({
      workspaceName: workspace.workspaceName,
      description: workspace.description || '',
      isActive: workspace.isActive
    });
    setShowEditModal(true);
  };

  const handleDeleteWorkspace = async (workspace) => {
    setSelectedWorkspace(workspace);
    setDeleteNumbers(['', '', '']);
    setDeleteError(null);
    
    // Check if workspace can be deleted
    try {
      const response = await apiService.checkWorkspaceCanDelete(workspace.workspaceId);
      if (response.success) {
        setGraphCount(response.graphCount || 0);
        setShowDeleteModal(true);
      }
    } catch (err) {
      setError('Failed to check workspace delete status');
    }
  };

  const handleManagePermissions = async (workspace) => {
    setSelectedWorkspace(workspace);
    setPermissions([]);
    setUserSearchTerm('');
    setSearchedUsers([]);
    setSelectedUser(null);
    setShowPermissionsModal(true);
    
    // Load permissions
    await loadPermissions(workspace.workspaceId);
  };

  const loadPermissions = async (workspaceId) => {
    try {
      setPermissionsLoading(true);
      const response = await apiService.listWorkspacePermissions(workspaceId);
      if (response.success) {
        setPermissions(response.permissions || []);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Failed to load permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleSearchUsers = async () => {
    if (!userSearchTerm.trim()) {
      setSearchedUsers([]);
      return;
    }
    
    try {
      const response = await apiService.searchUsers(userSearchTerm);
      if (response.success) {
        setSearchedUsers(response.users || []);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setError('Failed to search users');
    }
  };

  const handleGrantPermission = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }
    
    try {
      const response = await apiService.grantWorkspacePermission(
        selectedWorkspace.workspaceId,
        selectedUser.userId,
        selectedPermissionType
      );
      
      if (response.success) {
        setSuccess('Permission granted successfully');
        setSelectedUser(null);
        setUserSearchTerm('');
        setSearchedUsers([]);
        await loadPermissions(selectedWorkspace.workspaceId);
      } else {
        setError(response.message || 'Failed to grant permission');
      }
    } catch (err) {
      console.error('Error granting permission:', err);
      setError(err.response?.data?.message || 'Failed to grant permission');
    }
  };

  const handleRevokePermission = async (accessControlId) => {
    if (!window.confirm('Are you sure you want to revoke this permission?')) {
      return;
    }
    
    try {
      const response = await apiService.revokeWorkspacePermission(
        selectedWorkspace.workspaceId,
        accessControlId
      );
      
      if (response.success) {
        setSuccess('Permission revoked successfully');
        await loadPermissions(selectedWorkspace.workspaceId);
      } else {
        setError(response.message || 'Failed to revoke permission');
      }
    } catch (err) {
      console.error('Error revoking permission:', err);
      setError(err.response?.data?.message || 'Failed to revoke permission');
    }
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiService.createWorkspace(formData);
      
      if (response.success) {
        setSuccess('Workspace created successfully');
        setShowCreateModal(false);
        loadWorkspaces();
      } else {
        setError(response.message || 'Failed to create workspace');
      }
    } catch (err) {
      console.error('Error creating workspace:', err);
      setError(err.response?.data?.message || 'Failed to create workspace');
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await apiService.updateWorkspace(
        selectedWorkspace.workspaceId,
        formData
      );
      
      if (response.success) {
        setSuccess('Workspace updated successfully');
        setShowEditModal(false);
        loadWorkspaces();
      } else {
        setError(response.message || 'Failed to update workspace');
      }
    } catch (err) {
      console.error('Error updating workspace:', err);
      setError(err.response?.data?.message || 'Failed to update workspace');
    }
  };

  const handleSubmitDelete = async (e) => {
    e.preventDefault();
    
    const code = deleteNumbers.join('');
    
    if (code.length !== 3 || !/^\d{3}$/.test(code)) {
      setDeleteError('Please enter 3 digits');
      return;
    }
    
    if (graphCount > 0) {
      setDeleteError(`Cannot delete. Workspace contains ${graphCount} graph(s).`);
      return;
    }
    
    try {
      const response = await apiService.deleteWorkspace(
        selectedWorkspace.workspaceId,
        code
      );
      
      if (response.success) {
        setSuccess('Workspace deleted successfully');
        setShowDeleteModal(false);
        loadWorkspaces();
      } else {
        setDeleteError(response.message || 'Failed to delete workspace');
      }
    } catch (err) {
      console.error('Error deleting workspace:', err);
      setDeleteError(err.response?.data?.message || 'Failed to delete workspace');
    }
  };

  const handleDeleteNumberChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newNumbers = [...deleteNumbers];
      newNumbers[index] = value;
      setDeleteNumbers(newNumbers);
      
      // Auto-focus next input
      if (value && index < 2) {
        const nextInput = document.getElementById(`delete-digit-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="workspace-management">
      <div className="workspace-header">
        <div className="workspace-header-left">
          <h1 className="workspace-title">Workspace Management</h1>
          <p className="workspace-subtitle">Manage workspaces and their access permissions</p>
        </div>
        {canCreateWorkspaces && (
          <button 
            className="workspace-btn workspace-btn-primary"
            onClick={handleCreateWorkspace}
          >
            + Create Workspace
          </button>
        )}
      </div>

      {error && (
        <div className="workspace-alert workspace-alert-error">
          {error}
          <button className="workspace-alert-close" onClick={clearMessages}>×</button>
        </div>
      )}

      {success && (
        <div className="workspace-alert workspace-alert-success">
          {success}
          <button className="workspace-alert-close" onClick={clearMessages}>×</button>
        </div>
      )}

      <div className="workspace-controls">
        <input
          type="text"
          className="workspace-search"
          placeholder="Search workspaces..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="workspace-page-size">
          <label>Show:</label>
          <select 
            value={pageSize} 
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="workspace-loading">Loading workspaces...</div>
      ) : filteredWorkspaces.length === 0 ? (
        <div className="workspace-empty">
          <div className="workspace-empty-icon">📁</div>
          <h3>No Workspaces Found</h3>
          <p>
            {searchTerm ? 'No workspaces match your search.' : 'Get started by creating your first workspace.'}
          </p>
        </div>
      ) : (
        <>
          <div className="workspace-table-container">
            <table className="workspace-table">
              <thead>
                <tr>
                  <th>Workspace Name</th>
                  <th>Description</th>
                  <th>Graph Count</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkspaces.map((workspace) => (
                  <tr key={workspace.workspaceId}>
                    <td>
                      <strong>{workspace.workspaceName}</strong>
                    </td>
                    <td className="workspace-description">
                      {workspace.description || '—'}
                    </td>
                    <td>
                      <span className="workspace-badge workspace-badge-info">
                        {workspace.graphCount || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`workspace-status ${workspace.isActive ? 'workspace-status-active' : 'workspace-status-inactive'}`}>
                        {workspace.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="workspace-meta">{workspace.createUser}</td>
                    <td className="workspace-meta">{formatDate(workspace.createDate)}</td>
                    <td>
                      <div className="workspace-actions">
                        {workspace.canEdit && (
                          <button
                            className="workspace-action-btn workspace-action-btn-permissions"
                            onClick={() => handleManagePermissions(workspace)}
                            title="Manage Permissions"
                          >
                            🔐
                          </button>
                        )}
                        {workspace.canEdit && (
                          <button
                            className="workspace-action-btn workspace-action-btn-edit"
                            onClick={() => handleEditWorkspace(workspace)}
                            title="Edit Workspace"
                          >
                            ✏️
                          </button>
                        )}
                        {workspace.canDelete && (
                          <button
                            className="workspace-action-btn workspace-action-btn-delete"
                            onClick={() => handleDeleteWorkspace(workspace)}
                            title="Delete Workspace"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="workspace-pagination">
            <div className="workspace-pagination-info">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} workspaces
            </div>
            <div className="workspace-pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="workspace-pagination-btn"
              >
                Previous
              </button>
              <span className="workspace-pagination-current">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="workspace-pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <div className="workspace-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="workspace-modal" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-modal-header">
              <h2>Create New Workspace</h2>
              <button className="workspace-modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitCreate}>
              <div className="workspace-modal-body">
                <div className="workspace-form-group">
                  <label>Workspace Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.workspaceName}
                    onChange={(e) => setFormData({...formData, workspaceName: e.target.value})}
                    placeholder="Enter workspace name"
                  />
                </div>
                <div className="workspace-form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Enter workspace description"
                    rows={4}
                  />
                </div>
                <div className="workspace-form-group workspace-form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="workspace-modal-footer">
                <button 
                  type="button" 
                  className="workspace-btn workspace-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="workspace-btn workspace-btn-primary">
                  Create Workspace
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Workspace Modal */}
      {showEditModal && (
        <div className="workspace-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="workspace-modal" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-modal-header">
              <h2>Edit Workspace</h2>
              <button className="workspace-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitEdit}>
              <div className="workspace-modal-body">
                <div className="workspace-form-group">
                  <label>Workspace Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.workspaceName}
                    onChange={(e) => setFormData({...formData, workspaceName: e.target.value})}
                    placeholder="Enter workspace name"
                  />
                </div>
                <div className="workspace-form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Enter workspace description"
                    rows={4}
                  />
                </div>
                <div className="workspace-form-group workspace-form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>
              <div className="workspace-modal-footer">
                <button 
                  type="button" 
                  className="workspace-btn workspace-btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="workspace-btn workspace-btn-primary">
                  Update Workspace
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Workspace Modal */}
      {showDeleteModal && (
        <div className="workspace-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="workspace-modal workspace-modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-modal-header">
              <h2>Delete Workspace</h2>
              <button className="workspace-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitDelete}>
              <div className="workspace-modal-body">
                {graphCount > 0 ? (
                  <div className="workspace-alert workspace-alert-warning">
                    ⚠️ This workspace contains {graphCount} graph(s). Delete all graphs before deleting the workspace.
                  </div>
                ) : (
                  <>
                    <p>Are you sure you want to delete <strong>{selectedWorkspace?.workspaceName}</strong>?</p>
                    <p className="workspace-delete-confirm-text">
                      Enter the 3-digit confirmation code:
                    </p>
                    <div className="workspace-delete-inputs">
                      {[0, 1, 2].map((index) => (
                        <input
                          key={index}
                          id={`delete-digit-${index}`}
                          type="text"
                          maxLength="1"
                          value={deleteNumbers[index]}
                          onChange={(e) => handleDeleteNumberChange(index, e.target.value)}
                          className="workspace-delete-digit"
                        />
                      ))}
                    </div>
                    {deleteError && (
                      <div className="workspace-alert workspace-alert-error">
                        {deleteError}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="workspace-modal-footer">
                <button 
                  type="button" 
                  className="workspace-btn workspace-btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                {graphCount === 0 && (
                  <button type="submit" className="workspace-btn workspace-btn-danger">
                    Delete Workspace
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && (
        <div className="workspace-modal-overlay" onClick={() => setShowPermissionsModal(false)}>
          <div className="workspace-modal workspace-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-modal-header">
              <h2>Manage Permissions - {selectedWorkspace?.workspaceName}</h2>
              <button className="workspace-modal-close" onClick={() => setShowPermissionsModal(false)}>×</button>
            </div>
            <div className="workspace-modal-body">
              {/* Grant New Permission */}
              <div className="workspace-permissions-grant">
                <h3>Grant New Permission</h3>
                <div className="workspace-permissions-grant-form">
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearchUsers()}
                  />
                  <button 
                    type="button"
                    className="workspace-btn workspace-btn-secondary"
                    onClick={handleSearchUsers}
                  >
                    Search
                  </button>
                </div>
                
                {searchedUsers.length > 0 && (
                  <div className="workspace-user-search-results">
                    {searchedUsers.map(user => (
                      <div 
                        key={user.userId}
                        className={`workspace-user-item ${selectedUser?.userId === user.userId ? 'selected' : ''}`}
                        onClick={() => setSelectedUser(user)}
                      >
                        <div>
                          <strong>{user.firstName} {user.lastName}</strong>
                          <span className="workspace-user-email">{user.email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedUser && (
                  <div className="workspace-permissions-grant-actions">
                    <select 
                      value={selectedPermissionType}
                      onChange={(e) => setSelectedPermissionType(e.target.value)}
                    >
                      <option value="Read">Read</option>
                      <option value="Edit">Edit</option>
                      <option value="Delete">Delete</option>
                    </select>
                    <button 
                      type="button"
                      className="workspace-btn workspace-btn-primary"
                      onClick={handleGrantPermission}
                    >
                      Grant Permission
                    </button>
                  </div>
                )}
              </div>

              {/* Current Permissions */}
              <div className="workspace-permissions-list">
                <h3>Current Permissions</h3>
                {permissionsLoading ? (
                  <div className="workspace-loading">Loading permissions...</div>
                ) : permissions.length === 0 ? (
                  <p className="workspace-empty-text">No permissions set for this workspace.</p>
                ) : (
                  <table className="workspace-permissions-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Permission</th>
                        <th>Granted Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permissions.map(perm => (
                        <tr key={perm.accessControlId}>
                          <td>{perm.firstName} {perm.lastName}</td>
                          <td className="workspace-meta">{perm.email}</td>
                          <td>
                            <span className={`workspace-permission-badge workspace-permission-${perm.permissionName.toLowerCase()}`}>
                              {perm.permissionName}
                            </span>
                          </td>
                          <td className="workspace-meta">{formatDate(perm.grantedDate)}</td>
                          <td>
                            <button
                              className="workspace-action-btn workspace-action-btn-delete"
                              onClick={() => handleRevokePermission(perm.accessControlId)}
                              title="Revoke Permission"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="workspace-modal-footer">
              <button 
                type="button" 
                className="workspace-btn workspace-btn-secondary"
                onClick={() => setShowPermissionsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkspaceManagementPage;


